import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { quizApi, batchApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import { Button, Card, Metric, PageSkeleton, ErrorState, EmptyState } from '../components/ui';
import { quizStateLabel } from '../utils/quizState';
import { quizActions, totalAttempts } from '../utils/quizActions';
import QuizActionDialog from '../components/dialogs/QuizActionDialog';
import {
  formatPercent,
  formatScore,
  resultStatusLabel,
  resultStatusClass,
  accuracyTone,
  hardestQuestions,
  resultsToCsv,
  downloadTextFile,
} from '../utils/results';

// The page an institute opens the moment a test closes: who took it, who did
// not, and where everyone struggled.
export default function QuizResultsPage() {
  const { quizId } = useParams();
  const org = useOrg();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  // 'close' | 'edit' | 'delete' while its dialog is open.
  const [pending, setPending] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([quizApi.results(org.id, quizId), batchApi.list(org.id)])
      .then(([resultsRes, batchesRes]) => {
        setResults(resultsRes.data);
        setBatches(batchesRes.data || []);
        setError(null);
      })
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load results')))
      .finally(() => setLoading(false));
  }, [org.id, quizId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;
  if (!results) return <ErrorState message="No results found." />;

  const { quiz, summary, students, questions } = results;
  const batchName = batches.find((b) => b.id === quiz.batchId)?.name || quiz.batchId || '—';
  const hardest = hardestQuestions(questions, 3);
  const visible = students.filter((s) => filter === 'all' || s.status === filter);
  // Every student with an attempt has a row here, so this page knows exactly
  // whether anyone has started, which the test list does not.
  const attemptCount = totalAttempts(students);
  const actions = quizActions(quiz, { attemptCount });

  const afterAction = (action) => {
    setPending(null);
    if (action === 'delete') navigate('/manage-quiz');
    else load();
  };

  const exportCsv = () => {
    const safeName = (quiz.name || 'test').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    downloadTextFile(`${safeName}-results.csv`, resultsToCsv(results));
  };

  return (
    <>
      <div className="results-head">
        <div>
          <p className="breadcrumb"><Button variant="text" onClick={() => navigate('/manage-quiz')}>← All tests</Button></p>
          <h2 style={{ margin: 0 }}>{quiz.name}</h2>
          <div className="pills">
            <span className={quiz.state === 'open' ? 'status' : quiz.state === 'upcoming' ? 'pending' : 'status draft'}>{quizStateLabel(quiz.state)}</span>
            <span className="pill">{batchName}</span>
            <span className="pill">{quiz.questionCount} questions</span>
            {quiz.durationMinutes > 0 && <span className="pill">{quiz.durationMinutes} min</span>}
            <span className="pill">{quiz.allowedAttempts < 0 ? 'Unlimited attempts' : `${quiz.allowedAttempts} attempt${quiz.allowedAttempts === 1 ? '' : 's'}`}</span>
          </div>
        </div>
        <div className="header-actions">
          {actions.close.allowed && <Button variant="secondary" onClick={() => setPending('close')}>Close now</Button>}
          <Button variant="secondary" onClick={() => setPending('edit')}>Edit</Button>
          {actions.remove.allowed && <Button variant="danger" onClick={() => setPending('delete')}>Delete</Button>}
          <Button variant="secondary" onClick={load}>Refresh</Button>
          <Button variant="secondary" onClick={exportCsv} disabled={!students.length}>Download CSV</Button>
        </div>
      </div>

      <div className="metrics">
        <Metric label="Completed" value={`${summary.completed} / ${summary.expected}`} hint={`${summary.notStarted} not taken · ${summary.inProgress} in progress`} />
        <Metric label="Completion" value={formatPercent(summary.completionRate * 100)} hint="Of students in the batch" />
        <Metric label="Average score" value={formatScore(summary.averageScore)} hint={summary.averageScore == null ? 'No completed attempts yet' : `Best ${formatScore(summary.highestScore)} · Lowest ${formatScore(summary.lowestScore)}`} />
        <Metric label="Hardest question" value={hardest.length ? `Q${hardest[0].number}` : '—'} hint={hardest.length ? `${formatPercent(hardest[0].accuracy * 100)} answered correctly` : 'Needs completed attempts'} />
      </div>

      <div className="grid two">
        <Card className="table-card">
          <div className="section-title" style={{ padding: '17px 16px 0' }}>
            <div><h2>Students</h2><p>Highest score first, then those still to take it</p></div>
            <div className="tabs" style={{ border: 0, padding: 0, margin: 0 }}>
              {['all', 'completed', 'not_started'].map((f) => (
                <button key={f} className={filter === f ? 'selected' : ''} onClick={() => setFilter(f)}>
                  {f === 'all' ? 'All' : resultStatusLabel(f)}
                </button>
              ))}
            </div>
          </div>
          <table>
            <thead><tr><th>Student</th><th>Status</th><th>Score</th><th>Attempts</th></tr></thead>
            <tbody>
              {visible.length ? visible.map((s) => (
                <tr key={s.userId} className="clickable" onClick={() => navigate(`/students/${s.userId}`)}>
                  <td>
                    <b>{s.userName}</b>
                    <small>{s.userEmail}{!s.inBatch && ' · moved out of this batch'}</small>
                  </td>
                  <td><span className={resultStatusClass(s.status)}>{resultStatusLabel(s.status)}</span></td>
                  <td><b>{formatScore(s.score)}</b>{s.scoreDetails?.correct != null && s.scoreDetails?.totalQuestions != null && <small>{s.scoreDetails.correct}/{s.scoreDetails.totalQuestions} correct</small>}</td>
                  <td>{s.attemptCount}</td>
                </tr>
              )) : (
                <tr><td colSpan="4">
                  <EmptyState title={filter === 'all' ? 'No students in this batch' : 'Nobody here'}>
                    {filter === 'all' ? 'Add students to the batch and they will appear here.' : 'Try a different filter.'}
                  </EmptyState>
                </td></tr>
              )}
            </tbody>
          </table>
        </Card>

        <div>
          {hardest.length > 0 && (
            <Card style={{ padding: 16, marginBottom: 16 }}>
              <div className="section-title" style={{ marginBottom: 10 }}>
                <div><h2>Where the batch struggled</h2><p>Worth revisiting in class</p></div>
              </div>
              <div className="hardest">
                {hardest.map((q) => (
                  <div key={q.questionId} className="hardest-item">
                    <span className="checklist-marker">{q.number}</span>
                    <span className="question-snippet" title={q.text}>{q.text || 'Question'}</span>
                    <b>{formatPercent(q.accuracy * 100)}</b>
                  </div>
                ))}
              </div>
            </Card>
          )}
          <Card className="table-card">
            <div className="section-title" style={{ padding: '17px 16px 0' }}>
              <div><h2>Question by question</h2><p>Accuracy across completed attempts</p></div>
            </div>
            <table>
              <thead><tr><th>#</th><th>Question</th><th>Accuracy</th></tr></thead>
              <tbody>
                {questions.length ? questions.map((q) => {
                  const tone = accuracyTone(q.accuracy);
                  const pct = q.accuracy == null ? 0 : Math.round(q.accuracy * 100);
                  return (
                    <tr key={q.questionId}>
                      <td>{q.number}</td>
                      <td><span className="question-snippet" title={q.text}>{q.text || '—'}</span><small>{q.correct} right · {q.incorrect} wrong · {q.unattempted} skipped</small></td>
                      <td>
                        <div className={`accuracy ${tone}`}>
                          <i><em style={{ width: `${pct}%` }} /></i>
                          <b>{q.accuracy == null ? '—' : `${pct}%`}</b>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan="3"><EmptyState title="No questions">This test has no questions.</EmptyState></td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </div>
      </div>
      {pending && (
        <QuizActionDialog
          action={pending}
          quiz={quiz}
          attemptCount={attemptCount}
          onCancel={() => setPending(null)}
          onDone={afterAction}
        />
      )}
    </>
  );
}
